/**
 * Documents page — document accessibility scan results.
 *
 * Shows the run summary, per-document candidates, and PAC findings.
 * Clearly scoped: document scores are NOT included in the website
 * accessibility score.
 */

import { useParams, Link } from "react-router-dom";
import {
  useLatestDocuments,
  useDocumentCandidates,
  useDocumentFindings,
  useTriggerDocumentScan,
} from "../hooks/use-documents.js";
import { useFirstOrg } from "../hooks/use-orgs.js";
import { useBillingSummary } from "../hooks/use-billing.js";
import { WarningPanel } from "../components/warning-panel.js";
import { Breadcrumbs } from "../components/breadcrumbs.js";
import { PageSkeleton } from "../components/skeleton.js";

export function DocumentsPage() {
  const { websiteId } = useParams<{ websiteId: string }>();
  const org = useFirstOrg();
  const { data: billingData } = useBillingSummary(org?.id);
  const docsEnabled = billingData?.summary?.limits?.docsEnabled ?? false;

  const { data: run, isLoading: runLoading, isError: runError } = useLatestDocuments(websiteId);
  const { data: candidates, isLoading: candidatesLoading } =
    useDocumentCandidates(websiteId, run?.id);
  const { data: findings, isLoading: findingsLoading } = useDocumentFindings(
    websiteId,
    run?.id,
  );

  const scanMutation = useTriggerDocumentScan();

  const loading =
    runLoading || (run != null && (candidatesLoading || findingsLoading));

  if (loading) return <PageSkeleton />;

  // Upgrade prompt for plans without document scanning
  if (!docsEnabled) {
    return (
      <div className="product-page">
        <h1 className="page-title">Documents</h1>
        <div className="alert alert--info">
          <p>
            <strong>Document scanning</strong> is available on the Growth and
            Pro plans. Upgrade to scan PDFs and other documents for
            accessibility issues.
          </p>
          <Link to="/billing" className="btn btn--primary mt-2">
            Upgrade plan
          </Link>
        </div>
      </div>
    );
  }

  if (!run) {
    return (
      <div className="product-page">
        <h1 className="page-title">Documents</h1>
        {runError ? (
          <div className="alert alert--error" role="alert">
            Failed to load document data. Try refreshing the page.
          </div>
        ) : (
        <>
        <p className="text-muted">
          No document scan has been run yet. Click the button below to scan
          documents for accessibility issues.
        </p>
        <button
          type="button"
          className="btn btn--primary mt-2"
          disabled={scanMutation.isPending}
          onClick={() => {
            if (websiteId) scanMutation.mutate({ websiteId });
          }}
        >
          {scanMutation.isPending ? "Scanning…" : "Scan documents"}
        </button>
        {scanMutation.isError && (
          <p className="alert alert--error mt-2">
            Scan failed: {scanMutation.error?.message ?? "Unknown error"}
          </p>
        )}
        </>
        )}
      </div>
    );
  }

  const severityOrder = { error: 0, warning: 1, info: 2 } as const;

  const sortedFindings = [...(findings ?? [])].sort(
    (a, b) =>
      (severityOrder[a.severity] ?? 3) - (severityOrder[b.severity] ?? 3),
  );

  return (
    <div className="product-page">
      <Breadcrumbs
        items={[
          { label: "Websites", to: "/websites" },
          { label: "Website", to: `/websites/${websiteId}` },
          { label: "Documents" },
        ]}
      />
      <h1 className="page-title">Documents</h1>
      <p className="page-subtitle">
        Document accessibility findings are informational and are{" "}
        <strong>not included</strong> in the website accessibility score.
      </p>

      <button
        type="button"
        className="btn btn--secondary mb-3"
        disabled={scanMutation.isPending}
        onClick={() => {
          if (websiteId) scanMutation.mutate({ websiteId });
        }}
      >
        {scanMutation.isPending ? "Scanning…" : "Re-scan documents"}
      </button>
      {scanMutation.isError && (
        <p className="alert alert--error mt-2">
          Scan failed: {scanMutation.error?.message ?? "Unknown error"}
        </p>
      )}

      {/* ── Run summary ────────────────────────────────────────────────────── */}
      <section className="product-section" aria-labelledby="doc-summary-h">
        <h2 id="doc-summary-h" className="section-title">
          Run summary
        </h2>
        <div className="stat-grid">
          <div className="stat-item">
            <span className="stat-item__value">{run.documentsDetected}</span>
            <span className="stat-item__label">Detected</span>
          </div>
          <div className="stat-item">
            <span className="stat-item__value">{run.documentsScanned}</span>
            <span className="stat-item__label">Scanned</span>
          </div>
          <div className="stat-item">
            <span className="stat-item__value">{run.documentsFailed}</span>
            <span className="stat-item__label">Failed</span>
          </div>
          <div className="stat-item">
            <span className="stat-item__value">{run.findingsCount}</span>
            <span className="stat-item__label">Findings</span>
          </div>
        </div>
        {run.status === "skipped" && (
          <p className="alert alert--info mt-2">
            No documents were found in this discovery run.
          </p>
        )}
      </section>

      {/* ── Warnings ───────────────────────────────────────────────────────── */}
      {run.warnings.length > 0 && (
        <section className="product-section">
          <WarningPanel warnings={run.warnings} label="Document scan notices" />
        </section>
      )}

      {/* ── Candidates ─────────────────────────────────────────────────────── */}
      {candidates && candidates.length > 0 && (
        <section className="product-section" aria-labelledby="doc-candidates-h">
          <h2 id="doc-candidates-h" className="section-title">
            Documents ({candidates.length})
          </h2>
          <ul className="doc-list">
            {candidates.map((c) => (
              <li key={c.id} className={`doc-row doc-row--${c.status}`}>
                <div className="doc-row__url">
                  <a
                    href={c.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm"
                  >
                    {c.sourceUrl}
                  </a>
                </div>
                <div className="doc-row__meta text-sm text-muted">
                  <span>{c.fileType.toUpperCase()}</span>
                  <span>Status: {c.status}</span>
                  <span>Findings: {c.findingCount}</span>
                  {c.skipReason && <span>Skip reason: {c.skipReason}</span>}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ── Findings ───────────────────────────────────────────────────────── */}
      {sortedFindings.length > 0 && (
        <section className="product-section" aria-labelledby="doc-findings-h">
          <h2 id="doc-findings-h" className="section-title">
            Findings ({sortedFindings.length})
          </h2>
          <ul className="findings-list">
            {sortedFindings.map((f) => (
              <li
                key={f.id}
                className={`finding-row finding-row--${f.severity}`}
              >
                <span className="finding-row__severity">{f.severity}</span>
                <span className="finding-row__rule">{f.ruleId}</span>
                <span className="finding-row__message">{f.message}</span>
                {f.context && (
                  <span className="finding-row__context text-muted text-sm">
                    {f.context}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ── Related pages ────────────────────────────────────────────────── */}
      <section className="product-section">
        <div className="action-links">
          <Link
            to={`/websites/${websiteId}`}
            className="btn btn--secondary"
          >
            Dashboard
          </Link>
          <Link
            to={`/websites/${websiteId}/report`}
            className="btn btn--secondary"
          >
            Full report
          </Link>
        </div>
      </section>
    </div>
  );
}
